import { createContext, useContext, useState, useCallback, ReactNode } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { subscribeEvent } from "@/modules/Events/service/EventsService"
import { EventoDataType } from "@/types/events"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { CalendarSyncModal } from "@/modules/Calendar/components/CalendarSyncModal"
import { useIsMobile } from "@/hooks/use-mobile"
import { toast } from "sonner"

type SubscribeConfirmContextType = {
  requestSubscribe: (evento: EventoDataType, onSuccess?: () => void) => void
}

const SubscribeConfirmContext = createContext<SubscribeConfirmContextType | null>(null)

export function SubscribeConfirmProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const isMobile = useIsMobile()
  const [pending, setPending] = useState<EventoDataType | null>(null)
  const [onSuccessCallback, setOnSuccessCallback] = useState<(() => void) | null>(null)
  const [calendarModal, setCalendarModal] = useState<{ isOpen: boolean; evento: EventoDataType | null }>({
    isOpen: false,
    evento: null,
  })

  const requestSubscribe = useCallback((evento: EventoDataType, onSuccess?: () => void) => {
    setPending(evento)
    setOnSuccessCallback(() => onSuccess ?? null)
  }, [])

  const handleConfirm = () => {
    if (!pending || !user) return
    const evento = pending
    setPending(null)
    subscribeEvent({
      uid: user.pesUid,
      id_produto: evento.id,
      origem: evento.origem,
    })
      .then(() => {
        onSuccessCallback?.()
        // Integração de calendário não é exibida em dispositivos móveis
        if (!isMobile) setCalendarModal({ isOpen: true, evento })
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Erro ao se inscrever")
      })
  }

  return (
    <SubscribeConfirmContext.Provider value={{ requestSubscribe }}>
      {children}

      <AlertDialog
        open={!!pending}
        onOpenChange={(open) => { if (!open) setPending(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar inscrição</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja se inscrever em{" "}
              <span className="font-semibold text-foreground">{pending?.descricao}</span>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPending(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>Confirmar inscrição</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CalendarSyncModal
        isOpen={calendarModal.isOpen}
        evento={calendarModal.evento}
        onClose={() => setCalendarModal({ isOpen: false, evento: null })}
      />
    </SubscribeConfirmContext.Provider>
  )
}

export function useSubscribeConfirm() {
  const ctx = useContext(SubscribeConfirmContext)
  if (!ctx) throw new Error("useSubscribeConfirm must be used within SubscribeConfirmProvider")
  return ctx
}
